import { useState, useMemo, useEffect } from 'react';
import PulseLoader from 'react-spinners/PulseLoader';
import { toast } from 'react-toastify';

import { ModelTable, Button, SubHeader } from '@component';

import {
  collection,
  animalCollection,
  levelOneCollection,
  levelTwoCollection,
  levelThreeCollection,
} from './constants';
import { Item } from './Item';
import { useServices } from '@services';
import CONSTANT from 'constant';
import ExportCSV from './exportCSV';

export const MeatcutList = () => {
  const { db, auth } = useServices();
  const [item, setItem] = useState(null);
  const [updatedOn, setUpdatedOn] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [animals, setAnimalOptions] = useState([]);
  const [levelOneCategories, setLevelOneOptions] = useState([]);
  const [levelTwoCategories, setLevelTwoOptions] = useState([]);
  const [role, setRole] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [quizModifiedOn, setQuizModifiedOn] = useState();

  const canEdit = role === 'admin' || role === 'editor';

  useEffect(() => {
    auth.currentUser.getIdTokenResult().then(token => {
      setRole(token?.claims?.role || '');
    });
  }, [auth]);

  useEffect(() => {
    db.collection('quizData')
      .doc('modifiedOn')
      .get()
      .then(doc => {
        if (doc.exists) {
          setQuizModifiedOn(doc.data()?.modifiedOn);
        }
      })
      .catch(() => {
        toast.error('Failed to load quiz timestamp');
      });
  }, [db]);

  const newItem = {
    animalId: '',
    levelOneId: '',
    levelThreeId: '',
    levelTwoId: '',
    meatcutName: '',
    meatcutCode: '',
    meatcutDetail: '',
    meatcutDetail1: '',
    meatcutDetail2: '',
    modelUrl: '',
    modelFileName: '',
    modelFile: {
      name: '',
      size: '',
      url: '',
    },
    fileSize: 0,
    carcaseImages: [],
    otherImages: [],
    skeltonUrl: '',
    textureUrl: '',
    carcaseUrl: '',
    carcaseFileName: '',
    muscleRef: '-1',
    isActive: true,
  };

  useEffect(() => {
    const mounted = { current: true };
    setLoading(true);
    Promise.all([
      db.list(animalCollection),
      db.list(levelOneCollection),
      db.list(levelTwoCollection),
      db.list(levelThreeCollection),
    ])
      .then(([animalOptions, levelOneOptions, levelTwoOptions, levelThreeOptions]) => {
        if (!mounted.current) return;
        setAnimalOptions(animalOptions);
        setLevelOneOptions(levelOneOptions);
        setLevelTwoOptions(levelTwoOptions);
      })
      .catch(err => {
        if (mounted.current) toast.error('An unknown error occured');
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, [db]);

  const columns = useMemo(
    () => [
      {
        Header: '2D Image',
        accessor: ({ carcaseUrl, skeltonUrl }) => {
          const url = carcaseUrl || skeltonUrl;
          if (!url) return;
          return (
            <div className="flex justify-center">
              <img src={url} alt="Visual representation" height="60" width="90" />
            </div>
          );
        },
        id: 'image',
      },
      { Header: 'Code', accessor: 'meatcutCode' },
      { Header: 'Product Description', accessor: 'meatcutName' },
      {
        Header: 'Species',
        accessor: ({ animalId }) => {
          const category = animals.find(cat => {
            return cat.id === animalId;
          });
          return category ? category.name : '';
        },
        id: 'animalId',
      },
      {
        Header: 'Level 1',
        accessor: ({ levelOneId }) => {
          const category = levelOneCategories.find(cat => {
            return cat.id === levelOneId;
          });
          return category ? category.name.split('$').join(': ') : '';
        },
        id: 'levelOneId',
      },
      {
        Header: 'Level 2',
        accessor: ({ levelTwoId }) => {
          const category = levelTwoCategories.find(cat => cat.id === levelTwoId);
          return category ? category.name.split('$').join(': ') : '';
        },
        id: 'levelTwo',
      },
    ],
    [levelOneCategories, levelTwoCategories, animals]
  );

  return (
    <>
      <SubHeader title="Meat Products" />
      <div className="bg-white p-4 m-4 rounded-md mx-4 items-center">
        {loading ? (
          <PulseLoader></PulseLoader>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <div className="space-x-4">
                <input
                  type="text"
                  onChange={e => setFilterName(e.target.value)}
                  className="border border-gray-100 rounded-md p-2"
                  placeholder="Search Product Description"
                />
                <input
                  type="text"
                  onChange={e => setFilterCode(e.target.value)}
                  className="border border-gray-100 rounded-md p-2"
                  placeholder="Search Code"
                />
              </div>
              <ExportCSV db={db} databaseKey={CONSTANT.collection.dbMeatcut} fileName={'meatcuts.csv'}></ExportCSV>

              {canEdit && <Button onClick={() => setItem(newItem)}>Add Meat Products</Button>}
            </div>
            <ModelTable
              model={{ name: 'Meat Products' }}
              collection={collection}
              columns={columns}
              version={updatedOn}
              onViewRow={row => setItem({ ...newItem, ...row })}
              canDelete={canEdit}
              className="mt-4"
              defaultSortBy={[{ id: 'meatcutCode' }]}
              query={() => ({
                get: () =>
                  db
                    .collection(collection)
                    .orderBy('meatcutName')
                    .get()
                    .then(result => ({
                      docs: result.docs.filter(doc => {
                        const data = doc.data();
                        const nameMatch = filterName
                          ? (data.meatcutName || '').toLowerCase().includes(filterName.toLowerCase())
                          : true;
                        const codeMatch = filterCode
                          ? (data.meatcutCode || '').toLowerCase().includes(filterCode.toLowerCase())
                          : true;
                        return nameMatch && codeMatch;
                      }),
                    })),
              })}
            />
          </>
        )}
        <Item item={item} editable={canEdit} close={() => setItem(null)} saved={() => setUpdatedOn(new Date())}></Item>
        <div className="text-center text-gray-500 mt-4">
          Quiz data last modified: {quizModifiedOn ? new Date(quizModifiedOn * 1000).toLocaleString() : '-'}
        </div>
      </div>
    </>
  );
};
